<template>
<div
    role="button"
    tabindex="0"
    class="mobile-card"
    :class="`${variant}`"
    :style="{ backgroundImage: `url(${slide.spotlightMobile?.data?.attributes?.url})` }"
    v-on:click="onSlideClick(slide)"
    v-on:keydown.enter="onSlideClick(slide)"
>
    <div class="gradient-bottom" />
    <div class="overlay">
        <NuxtImg
            v-if="product"
            :src="product?.wordmark?.data?.attributes?.url"
            :alt="product.wordmark.data?.attributes?.alternativeText"
            class="logo"
            :class="slide.wordmarkSize"
        />
        <span v-if="slide.title" class="title">
            {{ slide.title }}
        </span>
        <div v-if="slide.statusLabel" class="platform-status">
            <span class="x-cap-13">{{ slide.statusLabel === 'OUT NOW' ? slide.statusLabel + ':' : slide.statusLabel }}</span>
            <div v-if="product && !product.displayWishlistUI" v-on:click.stop>
                <StorefrontIcons
                    v-if="platforms" :pack="product"
                    :platform-options="platforms"
                />
            </div>
        </div>
        <p
            class="blurb x-b-15"
            :style="{
                WebkitLineClamp: slide.type === 'general' ? 6 : 3
            }"
        >
            {{ slide.description }}
        </p>
        <ClientOnly>
            <div v-if="product && variant == 'variant-B' && product.displayWishlistUI">
                <a
                    class="b-variant"
                    :href="wishlistUrl"
                    :style="{ background: slide.cta?.color || defaultCtaColor }"
                    target="_blank"
                    rel="noopener noreferrer"
                    v-on:click.stop="trackWishlistClick"
                >
                    <PlatformIcon
                        platform="steam"
                        icon-color="white"
                        alt="steam"
                        :classes="['steam-icon']"
                    />
                    <span class="x-l-15">{{ slide.cta?.label }}</span>
                </a>
            </div>
        </ClientOnly>
    </div>
</div>
</template>

<script setup lang="ts">
import type { PropType } from 'vue'
import type { JBGWeb } from '$types/JBGWeb'
import PlatformIcon from '~/public/images/icons/platforms/PlatformIcon.vue'
import StorefrontIcons from '../shopping/StorefrontIcons.vue'
import { combineUtmParams } from '$services/tracking'
import { LaunchDarkly } from '$services/launchdarkly'
import Metric = LaunchDarkly.Metric

const props = defineProps({
    slide: {
        type: Object as PropType<JBGWeb.HomepageFeature>,
        required: true
    },
    variant: {
        type: String,
        default: 'variant-A'
    },
    platforms: {
        type: Array as PropType<JBGWeb.PlatformOption[]>
    }
})

const app = useNuxtApp()
const localeRoute = useLocaleRoute()
const route = useRoute()

const product = computed(() => {
    switch (props.slide.type) {
    case 'pack':
        return props.slide.packDetail.data.attributes
    case 'game':
        return props.slide.gameDetail.data.attributes
    case 'external-game':
        return props.slide.externalGame.data.attributes
    case 'bundle':
        return props.slide.bundleDetail.data.attributes
    default:
        return null
    }
})

const defaultCtaColor = 'linear-gradient(180deg, #DFE5EE 0%, #E1EAF2 50%, #DFE5EE 100%)'

function getSlideRoute(slide: JBGWeb.HomepageFeature) {
    if (slide.type === 'pack') {
        return localeRoute({
            name: 'games-packs-packslug',
            params: { packslug: slide.slug }
        })
    }

    if (slide.type === 'external-game') {
        return localeRoute({
            name: 'games-publishing-externalgameslug',
            params: { externalgameslug: slide.slug }
        })
    }

    return localeRoute({
        name: 'games-gameslug',
        params: { gameslug: slide.slug }
    })
}

function isExternalURL(url: string) {
    if (!url) return false
    try {
        return new URL(url).origin !== window.location.origin
    } catch {
        return false
    }
}

function generalSlideRoute(slide: JBGWeb.HomepageFeature) {
    if (typeof slide.cta.url === 'string' && isExternalURL(slide.cta.url)) {
        window.open(slide.cta.url, '_blank', 'noopener noreferrer')
    } else {
        // internal path so strip https://jackboxgames.com and use rest of path
        const internalPath = slide.cta.url.replace(window.location.origin, '')
        void navigateTo(internalPath)
    }
}

function onSlideClick(slide: JBGWeb.HomepageFeature) {
    if (slide.type === 'general') {
        generalSlideRoute(slide)
    } else {
        void navigateTo(getSlideRoute(slide))
    }
}

const wishlistUrl = computed(() => {
    if (!props.slide.cta?.url) return ''

    const variant = props.variant === 'variant-A' ? 'a' : 'b'

    const utmUrl = new URL(props.slide.cta.url)
    utmUrl.searchParams.set('utm_source', 'jbg-site')
    utmUrl.searchParams.set('utm_medium', 'mobile-homepage-carousel-slide')
    utmUrl.searchParams.set('utm_campaign', 'announcement-wishlisting')
    utmUrl.searchParams.set('utm_content', variant)

    combineUtmParams(utmUrl, new URLSearchParams(route.query as Record<string, string>))

    return utmUrl.toString()
})

function trackWishlistClick() {
    if (!wishlistUrl.value) return

    app.$launchDarkly.track(Metric.WishlistClickthrough, { url: wishlistUrl.value })
}

</script>

<style lang="scss" scoped>
@import "../../styles/kit.scss";

.mobile-card {
    width: 320px;
    height: 460px;
    scroll-snap-align: center;
    flex-shrink: 0;
    border-radius: 16px;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    position: relative;
    overflow: hidden;
    cursor: pointer;
    box-shadow: 0 3px 5px rgba(0, 0, 0, 0.3);

    &:last-child {
        margin-right: 16px;
    }

    .gradient-bottom {
        position: absolute;
        bottom: 0;
        width: 100%;
        height: 50%;
        border-bottom-left-radius: 16px;
        border-bottom-right-radius: 16px;
        overflow: hidden;
        background: linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.32) 40%, rgba(0, 0, 0, 0.48) 100%);
    }

    .overlay {
        display: flex;
        flex-direction: column;
        position: absolute;
        bottom: 0;
        width: 100%;
        padding: 0 14px 14px 14px;

        .logo {
            margin-bottom: 12px;
            object-fit: contain;
            max-height: 160px;

            &.large {
                width: 100%;
            }

            &.medium {
                width: 190px;
            }

            &.small {
                width: 180px;
            }
        }

        .title {
            color: #FFF;
            font-size: 28px;
            line-height: 28px;
            font-weight: 680;
            margin-bottom: 8px;
        }

        .platform-status {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 6px;
        }

        :deep(.steam-icon) {
            height: 22px;
            width: 22px;
        }

        .blurb {
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
            margin-bottom: 0px;
        }

        .b-variant {
            display: flex;
            justify-content: center;
            align-items: center;
            margin-top: 12px;
            border-radius: 8px;
            gap: 5px;
            cursor: pointer;
            width: 100%;
            border: none;
            padding: 8px 12px;

            &:hover {
                opacity: 0.9;
                transform: translateY(-2px);
            }
        }
    }
}

:deep(.plat-icons-container) {
    padding: 0px;
    gap: 5px;
}

:deep(.storefront-icon) {
    color: white;
    height: 18px !important;
    width: 18px !important;
    margin: 0px !important;
    &:hover {
        color: var(--primary-200)
    }
    @include mq-xsmall {
        margin: 0px !important;
    }
}
</style>
